/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-param-reassign */
import { EditorState, QueryMethods } from '@candulabs/craft';
import forIn from 'lodash/forIn';
import pickBy from 'lodash/pickBy';
import shortid from 'shortid';

import { Resolver } from 'craft/types';

import { richTextResolver, rteChildrenResolvers } from './constants';
import { deleteNodes } from './deleteNodes';
import { getSplitOperations, NodeInfo } from './getSplitOperations';

// Split a RTE node into multiple RTE nodes
// This happens when a non-RTE node (ie: a Button) is dropped into a Rte node
export const splitRTE = (state: EditorState) => {
  const rteNodes = pickBy(state.nodes, (node) => {
    if (node.data.type !== richTextResolver) {
      return false;
    }

    const parentId = node.data.parent;
    const parentNode = state.nodes[parentId];
    if (!parentNode) {
      return true;
    }

    const isParentRteNode = [richTextResolver, ...rteChildrenResolvers].includes(
      parentNode.data.type as Resolver,
    );

    if (isParentRteNode) {
      return false;
    }

    return true;
  });

  forIn(rteNodes, (rteNode) => {
    const splitOperations = getSplitOperations(
      state,
      rteNode.id,
      richTextResolver,
      rteChildrenResolvers,
    );

    const rteParentId = rteNode.data.parent;
    const rteParentNode = state.nodes[rteParentId];
    const rteIndexInParent = rteParentNode.data.nodes.indexOf(rteNode.id);

    const expelledIds = new Set();

    // If we are moving something, ensure the selection of the Rte node is set to null
    if (splitOperations.length > 0) {
      state.nodes[rteNode.id].data.custom.selection = null;
    }

    // Stores the latest generated Rte node id from a insert_tree op
    let lastGeneratedRteNodeId: string | null = null;

    splitOperations.forEach((splitOperation, i) => {
      const { type } = splitOperation;

      /**
       * Expel a non-Rte node
       *
       * Example:
       * Given the following state:
       * - Root
       *   - Rte
       *    - Typography
       *    - Button
       *
       * Transform it into:
       *
       * - Root
       *   - Rte
       *    - Typography
       *   - Button <-- expel moves a non-Rte child node out of the Rte node
       */
      if (type === 'expel') {
        lastGeneratedRteNodeId = null;
        const nodeIdToMove = splitOperation.id as string;
        const currentParentId = state.nodes[nodeIdToMove].data.parent;
        const currentParentNode = state.nodes[currentParentId];

        if (currentParentNode.data.nodes.indexOf(nodeIdToMove) > -1) {
          state.nodes[currentParentId].data.nodes.splice(
            currentParentNode.data.nodes.indexOf(nodeIdToMove),
            1,
          );
        }

        state.nodes[nodeIdToMove].data.parent = rteParentId;
        state.nodes[rteParentId].data.nodes.splice(rteIndexInParent + i + 1, 0, nodeIdToMove);

        return;
      }

      /**
       * Expel a non-Rte node
       *
       * Example:
       * Given the following state:
       * - Root
       *   - Rte
       *    - Typography
       *    - Button
       *    - Typography
       *
       * Transform it into:
       *
       * - Root
       *   - Rte
       *    - Typography
       *   - Button <-- expel moves a non-Rte child node out of the Rte node
       *   - Rte <-- insert_tree creates a new Rte node to wrap the remaining children nodes in a new Rte node
       *    - Typography
       */
      if (type === 'insert_tree' && splitOperation.tree) {
        const { tree } = splitOperation;
        const insertTree = ({ id, nodes, expelled }: NodeInfo, newParentId: string) => {
          const {
            props,
            custom: { selection, ...custom },
            type,
            parent: currentParentId,
          } = state.nodes[id].data;
          let idToUse = shortid();

          // If the current node we're adding is a Rte node type
          // Then append the new node Id after the original Rte ndoe
          if (id === rteNode.id) {
            lastGeneratedRteNodeId = idToUse;
            state.nodes[rteParentId].data.nodes.splice(rteIndexInParent + i + 1, 0, idToUse);
          } else if (expelled && expelledIds.has(id) === false) {
            idToUse = id;
            expelledIds.add(id);
            const currentParentNode = state.nodes[currentParentId];

            if (currentParentNode.data.nodes.indexOf(id) > -1) {
              state.nodes[currentParentId].data.nodes.splice(
                currentParentNode.data.nodes.indexOf(id),
                1,
              );
            }
          }

          state.nodes[idToUse] = QueryMethods(state)
            .parseFreshNode({
              id: idToUse,
              data: {
                type,
                props,
                custom,
                parent: newParentId,
                nodes: nodes.map((childNodeId: string) => insertTree(tree[childNodeId], idToUse)),
              },
            })
            .toNode();
          return idToUse;
        };

        const rootRteNode = tree[rteNode.id];
        insertTree(rootRteNode, rteParentId);
        return;
      }

      /**
       * Expand a nested Rte node into a parent Rte node
       *
       * Example:
       * Given the following state:
       * - Root
       *   - Rte
       *    - Typography
       *    - Rte
       *       - List
       *    - Typography
       *
       * Transform it into:
       *
       * - Root
       *   - Rte
       *    - Typography
       *    - List
       *    - Typography
       */
      if (type === 'expand' && splitOperation.id) {
        const nodeToExpand = state.nodes[splitOperation.id];

        // If we just inserted a new Rte node via insert_tree, use that as a parent
        // Otherwise, use the original Rte node parent
        const parentToExpandIn = state.nodes[lastGeneratedRteNodeId || nodeToExpand.data.parent];

        const nodeToExpandIndexInParent = parentToExpandIn.data.nodes.indexOf(nodeToExpand.id);

        nodeToExpand.data.nodes.forEach((nodeToExpandChildId, i) => {
          const index =
            nodeToExpandIndexInParent > -1
              ? nodeToExpandIndexInParent
              : parentToExpandIn.data.nodes.length - 1;

          state.nodes[parentToExpandIn.id].data.nodes.splice(index + i + 1, 0, nodeToExpandChildId);

          state.nodes[nodeToExpandChildId].data.parent = parentToExpandIn.id;
        });

        deleteNodes(state, [[nodeToExpand.id]]);
      }
    });
  });

  return state;
};
